import type { ArticlesList, Category } from 'packages/swf-help/center/src/types';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';

import { getCategoryTitle, getSideNavs, getVertical } from '../../utils';
import ArticleList from '../ArticleList/ArticleList';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import LoadMore from '../LoadMore/LoadMore';
import SideBar from '../SideBar/SideBar';

import { TXTWCL_Other_N0_help_categories } from './CategoryArticleList.messages.json';
import styles from './CategoryArticleList.scss';

interface Props {
  categories: Category[];
  categoryArticles: ArticlesList;
  pageSize: string;
  categoryId: string;
}

const CategoryArticleList: React.FC<Props> = ({
  categories,
  categoryArticles,
  pageSize,
  categoryId
}) => {
  const { articles: articlesList = [], meta } = categoryArticles || {};
  const [articles, setArticles] = useState(articlesList);
  const count = meta?.count || 0;

  const categoryTitle = getCategoryTitle(categories, categoryId);
  const sideNavs = getSideNavs(categories);
  const vertical = getVertical(useSelector(select.page.getVertical));
  const request = useRequest();

  useEffect(() => setArticles(articlesList), [articlesList]);

  const loadMore = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      const pagesCount = Math.ceil(articles.length / parseInt(pageSize, 10)) + 1;

      request({
        service: `help.articlesByCategoryId`,
        body: { category: categoryId, page: pagesCount, size: pageSize }
      }).subscribe(({ body: { articles: newArticles } }) => {
        setArticles([...articles, ...newArticles]);
      });
    },
    [articles, categoryId, pageSize, request]
  );

  return (
    <section>
      <div className={styles.inner}>
        <Breadcrumb id={categoryId} title={categoryTitle} />
        <h2 className={styles.title}>{categoryTitle}</h2>
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            <ArticleList articles={articles} regularHeight />
            <LoadMore len={articles.length} count={count} loadMore={loadMore} />
          </div>
          <div className={styles.sideBar}>
            <SideBar
              sideNavs={sideNavs}
              title={<Translation values={{ 0: vertical }} {...TXTWCL_Other_N0_help_categories} />}
              categoryId={categoryId}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
export default CategoryArticleList;
